/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect, useState } from 'react';

import { useCombobox, resetIdCounter } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';

import type { IInputDropdownProps } from '../_props';

import { useHoverOption } from './_useHoverOption';
import { useStateEvent } from './_useStateEvent';
import { useSearch } from './_useSearch';
import { useSelectItem } from './_useSelectItem';

type TUseDropdown = Pick<
  IInputDropdownProps,
  'onChangeText' | 'onChangeSelect' | 'options' | 'filterable' | 'isSelect' | 'id' | 'defaultValue'
>;

export const useDropdown = ({
  onChangeText,
  onChangeSelect,
  filterable,
  options,
  isSelect,
  id,
  defaultValue,
}: TUseDropdown) => {
  resetIdCounter();

  const [listOptions, setListOptions] = useState<IOptions[]>(options);

  useEffect(() => {
    setListOptions(options);
  }, [options]);

  /**
   * Composition Hooks
   */

  const { handleHoverOption } = useHoverOption({ listOptions });
  const { handleFocus, handleBlur, propagateImmediateSearch } = useStateEvent({ filterable, isSelect });
  const { handleSearchFilter, propagateSearch } = useSearch({ filterable, options, isSelect });
  const { handleSelectItem, propagateSelectItem } = useSelectItem({ filterable, options, isSelect });

  /**
   * Combobox Hook
   */

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    setInputValue,
    selectItem,
    getToggleButtonProps,
  } = useCombobox({
    id,
    items: listOptions,
    itemToString: (item) => (item ? item.label : ''),

    onHighlightedIndexChange: ({ highlightedIndex, type }) => {
      handleHoverOption({ type, highlightedIndex, setInputValue });
    },

    onStateChange: ({ selectedItem: toBeSelectedItem, type }) => {
      handleFocus({ type, inputValue, setListOptions, options });
      handleBlur({ type, inputValue, toBeSelectedItem, setInputValue, selectItem, onChangeText });

      propagateImmediateSearch({ type, inputValue, toBeSelectedItem, setInputValue, selectItem, onChangeText });
    },

    onInputValueChange: ({ inputValue, type }) => {
      handleSearchFilter({ type, inputValue, setListOptions });

      propagateSearch({ type, inputValue, onChangeText });
    },

    onSelectedItemChange: ({ selectedItem, type }) => {
      handleSelectItem({ selectedItem, setListOptions });

      propagateSelectItem({ selectedItem, onChangeSelect, type });
    },
    inputValue: defaultValue,
  });

  /**
   * Return
   */

  return {
    isOpen,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    listOptions,
    getToggleButtonProps,
  };
};

export default useDropdown;
